import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputAutocomplete from 'shared/components/Input/InputAutocomplete';
import { InputAutocompleteOption } from 'shared/components/Input/InputAutocomplete/InputAutocomplete';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { DeviceProfileT } from 'shared/models/device-profiles';
import { FlowT } from 'shared/models/flows';
import * as Source from 'shared/models/sources';
import api from 'utils/api';

import DeviceConfigItemFormMultipleIDs, { FormMultipleIDsEventEmitterT } from './components/DeviceConfigItemFormMultipleIDs';
import DeviceConfigItemFormOne from './components/DeviceConfigItemFormOne';
import DeviceConfigItemFormUploadCSV from './components/DeviceConfigItemUploadCSV';
import DeviceConfigRecommendations from './components/DeviceConfigRecommendations/DeviceConfigRecommendations';
import {
  APIFormBody,
  formEmptyValidator,
  formOneValidator,
  FormValuesT,
} from './DeviceConfigManageData.validator';

const CREATION_MODE_SIMPLE = 'simple';
const CREATION_MODE_MULTIPLE_IDS = 'multiple-ids';
const CREATION_MODE_UPLOAD_CSV = 'upload-csv';

function DeviceConfigCreate() {
  const { flowId } = useParams() as RouterParams;
  const [formValidator, setFormValidator] = useState(formEmptyValidator);
  const methods = useForm<FormValuesT>({
    mode: 'onChange',
    // useMemo allows to dynamically toggle the way of validating the form
    resolver: useMemo(() => yupResolver(formValidator), [formValidator]),
  });
  const [deviceProfileId, setDeviceProfileId] = useState<string | undefined>();
  const [creationMode, setCreationMode] = useState<string | undefined>();
  const [poolId, setPoolId] = useState('');
  const [sourceTechnology, setSourceTechnology] = useState('');
  const [loadingState, setLoadingState] = useState<number>(0);
  const nextLoadingState = () => setLoadingState((count) => count + 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenRecommendations, setIsOpenRecommendations] = useState(false);
  const history = useHistory();
  const formMultipleIDsRef = useRef<FormMultipleIDsEventEmitterT | null>(null);

  const {
    handleSubmit,
    setError,
    setValue,
  } = methods;

  // onValidateMultipleIDs: Allows to take actions depending on which part
  // of the submitted IDs are valid
  const onValidateMultipleIDs = (areValid: 'all' | 'none' | 'some') => {
    setIsSubmitting(false);
    if (areValid === 'none') return;
    if (areValid === 'all' || areValid === 'some') {
      if (areValid === 'all') {
        history.push(`/flows/${flowId}`);
      }
    }
  };

  const loadSourceFromSourceId = (targetSourceId: string) => {
    const request = api.sources.getOne(targetSourceId);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              history.push(`/flows/${flowId}`);
              nextLoadingState();
            });
        } else {
          resp.json()
            .then((source: Source.SourcesResultT) => {
              setValue('sourceTechnology', source.technology);
              setSourceTechnology(source.technology);
              nextLoadingState();
            });
        }
      });
  };

  const loadFlowData = () => {
    const request = api.flows.getOne(flowId as string);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
          nextLoadingState();
        } else {
          resp.json()
            .then((flow: FlowT) => {
              setPoolId(flow.poolId);
              loadSourceFromSourceId(flow.sourceId);
              nextLoadingState();
            });
        }
      });
  };

  useEffect(() => {
    loadFlowData();
  }, []);

  const onSubmit: SubmitHandler<FormValuesT> = (formData: FormValuesT) => {
    if (creationMode === CREATION_MODE_SIMPLE) {
      const apiBodyRequest: APIFormBody = {
        name: formData.name,
        comment: formData.comment,
        deviceId: formData.deviceId,
        addressIPv6: formData.addressIPv6,
        addressIPv4: formData.addressIPv4,
        isIPv4: formData.addressIPv4 !== '',
        deviceProfileId: deviceProfileId as string,
        poolId,
      };
      const apiParams = { flowId, deviceProfileId };
      const request = api.deviceConfig.register(apiBodyRequest, apiParams);
      request
        .then((resp: any) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                const errorMessage = error?.message;
                if (errorMessage === 'device ID already exists') {
                  setError('deviceId', { message: 'ID already used - Please enter a new one' });
                  return;
                }
                if (errorMessage === 'invalid IP address') {
                  setError('addressIPv4', { message: 'Invalid Address IP for this device' });
                  setError('addressIPv6', { message: 'Invalid Address IP for this device' });
                  return;
                }
                if (errorMessage === 'device IP already exists') {
                  setError('addressIPv4', { message: 'Address IP already used - Please enter a new one' });
                  setError('addressIPv6', { message: 'Address IP already used - Please enter a new one' });
                  return;
                }
                if (errorMessage === 'reservation pool is full') {
                  toast.error('No more ip address to allocate for that device');
                  return;
                }
                if (errorMessage.includes('exceed the device limit')) {
                  toast.warn('Creation not possible: Organization maximum number of devices reached');
                  return;
                }
                toast.error(errorMessage);
                setIsSubmitting(false);
              });
          } else {
            resp.json()
              .then(() => {
                toast.success('Device has been created');
                history.push(`/flows/${flowId}`);
                setIsSubmitting(false);
              });
          }
        });
    }
    if (creationMode === CREATION_MODE_MULTIPLE_IDS) {
      setIsSubmitting(true);
      formMultipleIDsRef.current?.onSubmit();
    }
  };

  const onChangeCreationMode = (_: any, value: string) => {
    setCreationMode(value);
    // Manage form validator
    if (value === CREATION_MODE_SIMPLE) {
      setFormValidator(formOneValidator);
    } else {
      setFormValidator(formEmptyValidator);
    }
  };

  const renderCreationMode = () => {
    if (deviceProfileId) {
      switch (creationMode) {
        case CREATION_MODE_SIMPLE:
          return (
            <DeviceConfigItemFormOne
              deviceProfileId={deviceProfileId as string}
              isEdit={false}
              sourceTechnology={sourceTechnology}
            />
          );
        case CREATION_MODE_MULTIPLE_IDS:
          return (
            <DeviceConfigItemFormMultipleIDs
              flowId={flowId as string}
              profileId={deviceProfileId}
              poolId={poolId}
              sourceTechnology={sourceTechnology}
              innerRef={formMultipleIDsRef}
              onValidateFn={onValidateMultipleIDs}
            />
          );
        case CREATION_MODE_UPLOAD_CSV:
          return (
            <DeviceConfigItemFormUploadCSV
              flowId={flowId as string}
              profileId={deviceProfileId}
            />
          );
        default:
          return null;
      }
    }
    return null;
  };

  if (loadingState < 2) {
    return <Loader.Page />;
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="title__header">
          <div className="title__header--block">
            <h1>Device · Create</h1>
            <Tooltip title="Read recommendations" arrow>
              <IconButton
                color="warning"
                size="medium"
                onClick={() => setIsOpenRecommendations(true)}
              >
                <InfoOutlinedIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
            <DeviceConfigRecommendations
              isOpen={isOpenRecommendations}
              onClose={() => setIsOpenRecommendations(false)}
            />
          </div>
          <ButtonSubmitData isSubmitting={isSubmitting} />
        </div>
        <div className="form scroll">
          <div className="form__section">
            <InputAutocomplete
              autocompleteName="device profile"
              grouped
              optionFormatFn={(rawOption: DeviceProfileT): InputAutocompleteOption => ({
                label: rawOption.name,
                groupBy: rawOption.templateName,
                ...rawOption,
              })}
              apiCall={api.deviceProfiles}
              apiParams={{ flowId }}
              required
              onChangeFn={(value: DeviceProfileT) => setDeviceProfileId(value?.id)}
            />
            {
              deviceProfileId
                ? (
                  <FormControl sx={{ m: 1 }}>
                    <FormLabel>Creation mode</FormLabel>
                    <RadioGroup onChange={onChangeCreationMode} row>
                      <FormControlLabel value={CREATION_MODE_SIMPLE} control={<Radio />} label="Single device" />
                      <FormControlLabel value={CREATION_MODE_MULTIPLE_IDS} control={<Radio />} label="Multiple devices" />
                      <FormControlLabel value={CREATION_MODE_UPLOAD_CSV} control={<Radio />} label="Upload CSV" />
                    </RadioGroup>
                  </FormControl>
                )
                : null
            }
          </div>
          {renderCreationMode()}
        </div>
      </form>
    </FormProvider>
  );
}

export default DeviceConfigCreate;
